import React from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  Show,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  useListContext,
  useShowContext,
  useDataProvider,
  useRedirect,
} from 'react-admin';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Plus, Pencil, Trash2, User } from 'lucide-react';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { RowActions } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';
import { validateLatitude, validateLongitude } from '../utils/validators';

/* ============================================================
   Locations — Latio design system (derived). List + Leaflet map + detail + CRUD.
   ============================================================ */

// Fix leaflet default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface LocationRecord {
  id: number;
  name?: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  user_id?: number;
  created_at?: string;
  updated_at?: string;
}

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

/* ---- Maps ---- */
const LocationMap: React.FC<{ latitude: number; longitude: number; name: string; height?: number }> = ({ latitude, longitude, name, height = 320 }) => (
  <div className="leaflet-map-container overflow-hidden rounded-card border border-hairline" style={{ height }}>
    <MapContainer center={[latitude, longitude]} zoom={15} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker position={[latitude, longitude]}>
        <Popup>{name}</Popup>
      </Marker>
    </MapContainer>
  </div>
);

const AllLocationsMap = () => {
  const { data, isLoading } = useListContext<LocationRecord>();
  const pts = (data || []).filter((l) => typeof l.latitude === 'number' && typeof l.longitude === 'number');
  if (isLoading || pts.length === 0) return null;
  const lats = pts.map((l) => l.latitude as number);
  const lngs = pts.map((l) => l.longitude as number);
  const latPad = (Math.max(...lats) - Math.min(...lats)) * 0.1 || 0.01;
  const lngPad = (Math.max(...lngs) - Math.min(...lngs)) * 0.1 || 0.01;
  const bounds: [[number, number], [number, number]] = [
    [Math.min(...lats) - latPad, Math.min(...lngs) - lngPad],
    [Math.max(...lats) + latPad, Math.max(...lngs) + lngPad],
  ];
  return (
    <div className="leaflet-map-container relative z-[1] h-[440px] overflow-hidden rounded-card border border-hairline">
      <MapContainer bounds={bounds} style={{ height: '100%', width: '100%' }} zoomControl>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pts.map((l) => (
          <Marker key={l.id} position={[l.latitude as number, l.longitude as number]}>
            <Popup>
              <strong>{l.name}</strong>
              {l.description && <p>{l.description}</p>}
              <small>ID: {l.id}</small>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

/* ---- List ---- */
const LOCATION_SORT = [
  { field: 'created_at', label: 'Created' },
  { field: 'name', label: 'Name' },
  { field: 'id', label: 'ID' },
];
const LOCATION_SORT_LABELS = Object.fromEntries(LOCATION_SORT.map((f) => [f.field, f.label]));

const NewLocationButton = () => {
  const redirect = useRedirect();
  return (
    <button
      type="button"
      onClick={() => redirect('create', 'locations')}
      className="inline-flex h-9 items-center gap-2 rounded-pill bg-black px-4 text-sm font-semibold text-white hover:bg-ink"
    >
      <Plus className="size-4" />
      New location
    </button>
  );
};

function InlineDeleteLocation({ location }: { location: LocationRecord }) {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const redirect = useRedirect();
  const { refetch } = useListContext();
  const [open, setOpen] = React.useState(false);
  const remove = async () => {
    try {
      await dataProvider.delete('locations', { id: location.id, previousData: location });
      toastSuccess('Location deleted');
      refetch();
    } catch {
      toastError('Failed to delete location');
    }
    void redirect;
  };
  return (
    <RowActions>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Delete location"
        className="inline-flex size-8 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error"
      >
        <Trash2 className="size-4" />
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        tone="destructive"
        title={`Delete "${location.name || `Location #${location.id}`}"?`}
        consequence="This permanently deletes the location. This cannot be undone."
        confirmLabel="Delete location"
        onConfirm={remove}
      />
    </RowActions>
  );
}

const LocationsListBody = () => {
  const columns: DataColumn<LocationRecord>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'name',
      header: 'Location',
      sortable: true,
      cell: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-latio-sm bg-indigo-tint text-indigo">
            <MapPin className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="truncate font-semibold text-ink">{r.name || `Location #${r.id}`}</div>
            {r.description && <div className="truncate text-[13px] text-ink-3">{r.description}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'coordinates',
      header: 'Coordinates',
      sortable: true,
      sortField: 'latitude',
      cell: (r) => (
        <span className="font-mono text-[12px] text-ink-2">
          {typeof r.latitude === 'number' ? r.latitude.toFixed(4) : '—'}, {typeof r.longitude === 'number' ? r.longitude.toFixed(4) : '—'}
        </span>
      ),
    },
    {
      key: 'user',
      header: 'Created by',
      cell: (r) =>
        r.user_id ? (
          <Link to={`/users/${r.user_id}/show`} onClick={(e) => e.stopPropagation()} className="text-indigo hover:text-indigo-press">
            User #{r.user_id}
          </Link>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    },
    { key: 'created_at', header: 'Created', sortable: true, cell: (r) => fmtDate(r.created_at) },
    { key: 'actions', header: '', cell: (r) => <InlineDeleteLocation location={r} /> },
  ];
  return (
    <>
      <div className="px-6 pt-6">
        <AllLocationsMap />
      </div>
      <ListScreen
        actions={
          <>
            <SortMenu fields={LOCATION_SORT} />
            <NewLocationButton />
          </>
        }
        tokens={<FilterTokens fields={[]} sortLabels={LOCATION_SORT_LABELS} />}
      >
        <DataTable columns={columns} rowClick="show" emptyMessage="No locations found." />
        <div className="pt-1">
          <ReactAdminPagination />
        </div>
      </ListScreen>
    </>
  );
};

export const LocationList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'created_at', order: 'DESC' }} perPage={25} component="div">
    <SearchIntegration filterKey="q" />
    <LocationsListBody />
  </List>
);

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, children, className }: { title?: string; icon?: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

const LocationShowContent = () => {
  const { record, isLoading } = useShowContext<LocationRecord>();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const redirect = useRedirect();
  const { setData } = useBreadcrumbContext();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (record) setData({ resourceId: record.id.toString(), resourceName: record.name || `Location #${record.id}`, resourceType: 'session' });
  }, [record, setData]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading location…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Location not found</div>;

  const remove = async () => {
    try {
      await dataProvider.delete('locations', { id: record.id, previousData: record });
      toastSuccess('Location deleted');
      redirect('list', 'locations');
    } catch {
      toastError('Failed to delete location');
    }
  };

  const hasCoords = typeof record.latitude === 'number' && typeof record.longitude === 'number';

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex size-[60px] items-center justify-center rounded-card bg-indigo-tint text-indigo">
            <MapPin className="size-7" />
          </span>
          <div>
            <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
              {record.name || `Location #${record.id}`}
            </h1>
            <p className="mt-1 text-sm text-ink-2">Location #{record.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => redirect('edit', 'locations', record.id)}
            className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
          >
            <Pencil className="size-4" />
            Edit
          </button>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-error px-4 text-sm font-semibold text-white hover:bg-error/90"
          >
            <Trash2 className="size-4" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          {hasCoords && <LocationMap latitude={record.latitude as number} longitude={record.longitude as number} name={record.name || 'Location'} height={360} />}
          <DetailCard title="Description">
            <p className="text-sm text-ink">{record.description || 'No description available'}</p>
          </DetailCard>
        </div>

        <div className="space-y-6">
          <DetailCard title="Coordinates" icon={MapPin}>
            <div className="divide-y divide-hairline">
              <Fact label="Latitude" value={record.latitude?.toFixed(6) ?? '—'} />
              <Fact label="Longitude" value={record.longitude?.toFixed(6) ?? '—'} />
            </div>
          </DetailCard>

          <DetailCard title="Created by" icon={User}>
            {record.user_id ? (
              <Link to={`/users/${record.user_id}/show`} className="font-semibold text-indigo hover:text-indigo-press">
                User #{record.user_id}
              </Link>
            ) : (
              <p className="text-sm text-ink-2">—</p>
            )}
          </DetailCard>

          <DetailCard title="Timeline">
            <div className="divide-y divide-hairline">
              <Fact label="Created" value={record.created_at ? new Date(record.created_at).toLocaleString() : '—'} />
              <Fact label="Updated" value={record.updated_at ? new Date(record.updated_at).toLocaleString() : '—'} />
            </div>
          </DetailCard>
        </div>
      </div>

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        tone="destructive"
        title={`Delete "${record.name || `Location #${record.id}`}"?`}
        consequence="This permanently deletes the location. This cannot be undone."
        confirmLabel="Delete location"
        onConfirm={remove}
      />
    </div>
  );
};

export const LocationShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <LocationShowContent />
  </Show>
);

/* ---- Edit / Create (RA forms) ---- */
export const LocationEdit = (props: any) => (
  <Edit {...props} mutationMode="pessimistic">
    <SimpleForm>
      <Card>
        <CardHeader>
          <CardTitle>Edit location</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <TextInput source="name" required fullWidth />
          <TextInput source="description" multiline rows={3} fullWidth />
          <div className="flex gap-4">
            <NumberInput source="latitude" required step={0.000001} fullWidth validate={validateLatitude} />
            <NumberInput source="longitude" required step={0.000001} fullWidth validate={validateLongitude} />
          </div>
          <ReferenceInput source="user_id" reference="users">
            <SelectInput optionText="name" fullWidth />
          </ReferenceInput>
        </CardContent>
      </Card>
    </SimpleForm>
  </Edit>
);

export const LocationCreate = (props: any) => (
  <Create {...props}>
    <SimpleForm>
      <Card>
        <CardHeader>
          <CardTitle>Create location</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <TextInput source="name" required fullWidth />
          <TextInput source="description" multiline rows={3} fullWidth />
          <div className="flex gap-4">
            <NumberInput source="latitude" required step={0.000001} helperText="e.g., 6.2476" fullWidth validate={validateLatitude} />
            <NumberInput source="longitude" required step={0.000001} helperText="e.g., -75.5658" fullWidth validate={validateLongitude} />
          </div>
          <ReferenceInput source="user_id" reference="users">
            <SelectInput optionText="name" fullWidth />
          </ReferenceInput>
          <div className="mt-4 text-sm text-ink-2">
            💡 Tip: In Google Maps, right-click a spot and copy its coordinates.
          </div>
        </CardContent>
      </Card>
    </SimpleForm>
  </Create>
);
